<template>
  <div>
    <nav class="landing-nav" :class="{ scrolled: isScrolled, 'menu-open': menuOpen }">
      <div class="nav-inner">
        <NuxtLink :to="localePath('/')" class="nav-logo">
          <AppLogo />
        </NuxtLink>

        <!-- Desktop -->
        <div class="nav-actions desktop-only">
          <!-- Shop lives on the shop.* subdomain — cross-host link so
               the user lands directly on the storefront, not the apex
               landing. -->
          <a :href="shopUrl" class="nav-shop-link">
            <NuxtIcon name="mdi:shopping-outline" class="nav-shop-icon" />
            Shop
          </a>
          <div class="language-switcher">
            <template v-for="(code, i) in LANG_CODES" :key="code">
              <span v-if="i" class="lang-divider">|</span>
              <NuxtLink :to="switchLocalePath(code)" class="lang-btn" :class="{ active: locale === code }">{{ code.toUpperCase() }}</NuxtLink>
            </template>
          </div>
          <template v-if="loggedIn && user">
            <a :href="dashboardUrl" class="btn-cyber-primary btn-nav">
              {{ $t('landing.nav_dashboard') }}
            </a>
          </template>
          <template v-else>
            <NuxtLink :to="localePath('/auth')" class="btn-cyber-secondary btn-nav">
              {{ $t('landing.nav_login') }}
            </NuxtLink>
            <NuxtLink :to="localePath('/auth') + '?tab=signup'" class="btn-cyber-primary btn-nav">
              {{ $t('landing.nav_get_started') }}
            </NuxtLink>
          </template>
        </div>

        <!-- Mobile hamburger -->
        <button
          ref="menuBtnRef"
          class="mobile-menu-btn mobile-only"
          :aria-label="menuOpen ? $t('landing.nav_menu_close') : $t('landing.nav_menu_open')"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          aria-controls="landing-mobile-menu"
          @click="menuOpen = !menuOpen"
        >
          <span class="hamburger-line" :class="{ open: menuOpen }"></span>
        </button>
      </div>
    </nav>

    <!-- Mobile overlay -->
    <Transition name="menu-fade">
      <div
        v-if="menuOpen"
        id="landing-mobile-menu"
        ref="menuRef"
        class="mobile-menu"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('landing.nav_menu_open')"
        @click.self="menuOpen = false"
        @keydown="onMenuKeydown"
      >
        <div class="menu-bg" aria-hidden="true">
          <div class="menu-orb menu-orb-1"></div>
          <div class="menu-orb menu-orb-2"></div>
          <div class="menu-grid"></div>
        </div>

        <nav class="menu-nav">
          <template v-for="(item, i) in menuItems" :key="item.to">
            <a
              v-if="item.anchor"
              :href="item.to"
              class="menu-link"
              :style="{ '--i': i }"
              @click="onAnchorClick($event, item.to)"
            >
              <span class="menu-link-num">{{ String(i + 1).padStart(2, '0') }}</span>
              <span class="menu-link-text">{{ item.label }}</span>
              <span class="menu-link-arrow">→</span>
            </a>
            <a
              v-else-if="item.external"
              :href="item.to"
              class="menu-link"
              :style="{ '--i': i }"
              @click="menuOpen = false"
            >
              <span class="menu-link-num">{{ String(i + 1).padStart(2, '0') }}</span>
              <span class="menu-link-text">{{ item.label }}</span>
              <span class="menu-link-arrow">→</span>
            </a>
            <NuxtLink
              v-else
              :to="item.to"
              class="menu-link"
              :style="{ '--i': i }"
              @click="menuOpen = false"
            >
              <span class="menu-link-num">{{ String(i + 1).padStart(2, '0') }}</span>
              <span class="menu-link-text">{{ item.label }}</span>
              <span class="menu-link-arrow">→</span>
            </NuxtLink>
          </template>
        </nav>

        <div class="menu-foot">
          <div class="menu-actions">
            <template v-if="loggedIn && user">
              <a :href="dashboardUrl" class="btn-cyber-primary btn-menu" @click="menuOpen = false">
                {{ $t('landing.nav_dashboard') }}
              </a>
            </template>
            <template v-else>
              <NuxtLink :to="localePath('/auth')" class="btn-cyber-secondary btn-menu" @click="menuOpen = false">
                {{ $t('landing.nav_login') }}
              </NuxtLink>
              <NuxtLink :to="localePath('/auth') + '?tab=signup'" class="btn-cyber-primary btn-menu" @click="menuOpen = false">
                {{ $t('landing.nav_get_started') }}
              </NuxtLink>
            </template>
          </div>

          <div class="menu-meta">
            <div class="menu-lang">
              <NuxtLink
                v-for="code in LANG_CODES"
                :key="code"
                :to="switchLocalePath(code)"
                class="lang-chip"
                :class="{ active: locale === code }"
                @click="menuOpen = false"
              >{{ code.toUpperCase() }}</NuxtLink>
            </div>

            <div v-if="socialLinks.length" class="menu-socials">
              <a
                v-for="s in socialLinks"
                :key="s.key"
                :href="s.url"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="s.label"
                class="menu-social-btn"
              >
                <NuxtIcon :name="s.icon" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
const { user, loggedIn } = useAuth()
const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()
const { locale, t } = useI18n()
const LANG_CODES = ['en', 'de', 'pt', 'es']
const { socials, tenant } = useTenantTheme()

const isScrolled = ref(false)
const menuOpen = ref(false)
const menuRef = ref(null)
const menuBtnRef = ref(null)

const dashboardUrl = computed(() => {
    const u = user.value
    if (!u) return '/'
    if (u.role === 'admin') return getSubdomainUrl('admin')
    // Label-manager tier (formalized role + legacy `creator + canCreateLabels=true`)
    // lands on labels.{tenant}; sub-users + basic creators on creator.{tenant}.
    const isLabelManager =
        !u.parentCreator
        && (u.role === 'label_manager'
            || (u.role === 'creator' && u.canCreateLabels === true))
    if (isLabelManager) return getSubdomainUrl('labels')
    if (u.role === 'creator' || u.role === 'label_manager') return getSubdomainUrl('creator')
    // Customers have no panel on the main domain. `/account` is a set of
    // standalone sub-pages (balance / preferences / security) shared by
    // the panels — there has never been an index, so this pointed at a
    // URL that is not a page and the catch-all answered it with a 200.
    // Their home is the storefront, where orders, library, playlists and
    // account settings all live.
    return getSubdomainUrl('shop', '/orders')
})

// Shop lives on shop.{tenant} — link absolute so the mobile menu and
// desktop button both cross the subdomain boundary cleanly.
const shopUrl = computed(() => getSubdomainUrl('shop', '/'))

const menuItems = computed(() => {
    if (tenant.value?.slug === 'psyfeature') {
        return [
            { to: shopUrl.value, label: 'Shop', external: true },
            { to: '#plans', label: t('landing.cta_plans'), anchor: true },
            { to: localePath('/imprint'), label: t('imprint.title') || 'Imprint', anchor: false },
            { to: localePath('/terms'), label: t('terms.title') || 'Terms', anchor: false }
        ]
    }
    return [
        { to: shopUrl.value, label: 'Shop', external: true },
        { to: '#features', label: t('landing.features_label'), anchor: true },
        { to: '#distribution', label: t('landing.dist_label'), anchor: true },
        { to: '#showcase', label: 'Shoplinks', anchor: true },
        { to: '#pricing', label: t('landing.cta_plans'), anchor: true },
        { to: localePath('/imprint'), label: t('imprint.title') || 'Imprint', anchor: false }
    ]
})

const onAnchorClick = (e, target) => {
    e.preventDefault()
    menuOpen.value = false
    // wait for menu close + body overflow restore before scrolling
    setTimeout(() => {
        const el = typeof document !== 'undefined' ? document.querySelector(target) : null
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 320)
}

const socialIconMap = {
    instagram: { icon: 'mdi:instagram', label: 'Instagram' },
    tiktok: { icon: 'simple-icons:tiktok', label: 'TikTok' },
    youtube: { icon: 'mdi:youtube', label: 'YouTube' },
    twitter: { icon: 'simple-icons:x', label: 'X' },
    soundcloud: { icon: 'mdi:soundcloud', label: 'SoundCloud' },
    spotify: { icon: 'mdi:spotify', label: 'Spotify' },
    discord: { icon: 'simple-icons:discord', label: 'Discord' },
    facebook: { icon: 'mdi:facebook', label: 'Facebook' }
}

const socialLinks = computed(() => {
    const s = socials.value || {}
    return Object.entries(socialIconMap)
        .filter(([key]) => !!s[key])
        .map(([key, meta]) => ({ key, url: s[key], ...meta }))
})

const onScroll = () => {
    isScrolled.value = window.scrollY > 80
}

// ── Mobile menu a11y ────────────────────────────────────────────────
// The overlay is a modal dialog: ESC closes it, Tab is trapped inside,
// and focus returns to the hamburger button on close.
const onWindowKeydown = (e) => {
    if (e.key === 'Escape' && menuOpen.value) menuOpen.value = false
}

const onMenuKeydown = (e) => {
    if (e.key !== 'Tab' || !menuRef.value) return
    const focusables = menuRef.value.querySelectorAll('a[href], button:not([disabled])')
    if (!focusables.length) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
    }
}

let prevBodyOverflow = ''
watch(menuOpen, (open) => {
    if (typeof document === 'undefined') return
    if (open) {
        prevBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onWindowKeydown)
        // Move focus into the dialog so the Tab trap engages immediately.
        nextTick(() => {
            const target = menuRef.value?.querySelector('a[href], button:not([disabled])')
            if (target) target.focus()
        })
    } else {
        document.body.style.overflow = prevBodyOverflow
        window.removeEventListener('keydown', onWindowKeydown)
        // Return focus to the control that opened the dialog.
        nextTick(() => menuBtnRef.value?.focus())
    }
})

onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true })
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('keydown', onWindowKeydown)
    if (typeof document !== 'undefined' && menuOpen.value) {
        document.body.style.overflow = prevBodyOverflow
    }
})
</script>

<style scoped>
.landing-nav {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  border-bottom: 1px solid transparent;
  /* iOS notch / dynamic island — keep the bar content out of the inset. */
  padding-top: env(safe-area-inset-top, 0px);
}

.landing-nav.scrolled {
  background: rgba(3, 3, 4, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom-color: var(--glass-border);
}

/* when menu is open, force nav to solid bg so logo + hamburger-X are always visible */
.landing-nav.menu-open {
  background: rgba(3, 3, 4, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-bottom-color: var(--glass-border) !important;
}

.landing-nav.menu-open .nav-inner {
  height: 60px;
}

.landing-nav.scrolled .nav-inner {
  height: 60px;
}

.landing-nav.scrolled :deep(.logo-img),
.landing-nav.menu-open :deep(.logo-img) {
  height: 34px;
}

.landing-nav.scrolled :deep(.app-logo--psy .logo-img),
.landing-nav.menu-open :deep(.app-logo--psy .logo-img) {
  height: 40px;
}

@media (max-width: 768px) {
  .landing-nav.scrolled :deep(.logo-img),
  .landing-nav.menu-open :deep(.logo-img) {
    height: 30px;
  }
  .landing-nav.scrolled :deep(.app-logo--psy .logo-img),
  .landing-nav.menu-open :deep(.app-logo--psy .logo-img) {
    height: 36px;
  }
}

.nav-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 calc(var(--space-6) + env(safe-area-inset-right, 0px)) 0 calc(var(--space-6) + env(safe-area-inset-left, 0px));
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-logo {
  display: flex;
  align-items: center;
  min-height: 44px;
  min-width: 44px;
  position: relative;
  z-index: 2;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.btn-nav {
  height: 44px !important;
  padding: 0 20px !important;
  font-size: 0.7rem !important;
}

.nav-shop-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-muted);
  text-decoration: none;
  padding: 6px 10px;
  border-radius: var(--radius-sm);
  transition: color 0.2s ease, background 0.2s ease;
}

.nav-shop-link:hover {
  color: var(--primary);
  background: rgba(255, 255, 255, 0.04);
}

.nav-shop-icon {
  font-size: 1rem;
  line-height: 1;
}

.language-switcher {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-right: var(--space-2);
}

.lang-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* muted not dim — dim fails AA contrast for interactive labels */
  color: var(--text-muted);
  font-size: 0.75rem;
  font-weight: 600;
  /* Padding stretches the hit area to >=44px without visually
     inflating the 72px bar. */
  min-width: 44px;
  padding: 14px 8px;
  border-radius: var(--radius-sm);
  transition: all 0.2s ease;
}

.lang-btn:hover {
  color: var(--text-main);
}

.lang-btn.active {
  color: var(--primary);
}

.lang-divider {
  color: var(--border);
  font-size: 0.75rem;
  opacity: 0.5;
}

/* Mobile menu button — 44×44 touch target */
.mobile-menu-btn {
  background: none;
  border: none;
  width: 44px;
  height: 44px;
  margin-right: -8px; /* keep the icon optically flush with the edge padding */
  position: relative;
  z-index: 101;
  display: none;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
}

.hamburger-line,
.hamburger-line::before,
.hamburger-line::after {
  display: block;
  width: 22px;
  height: 2px;
  background: var(--text-main);
  border-radius: 2px;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.hamburger-line {
  position: relative;
}

.hamburger-line::before,
.hamburger-line::after {
  content: '';
  position: absolute;
  left: 0;
}

.hamburger-line::before {
  top: -7px;
}

.hamburger-line::after {
  top: 7px;
}

.hamburger-line.open {
  background: transparent;
}

.hamburger-line.open::before {
  top: 0;
  transform: rotate(45deg);
}

.hamburger-line.open::after {
  top: 0;
  transform: rotate(-45deg);
}

/* Mobile overlay */
.mobile-menu {
  position: fixed;
  inset: 0;
  z-index: 99;
  display: flex;
  flex-direction: column;
  padding: calc(60px + env(safe-area-inset-top, 0px) + var(--space-4)) var(--space-6) calc(env(safe-area-inset-bottom, 0px) + var(--space-6));
  background: rgba(3, 3, 4, 0.96);
  backdrop-filter: blur(32px) saturate(140%);
  -webkit-backdrop-filter: blur(32px) saturate(140%);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.menu-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.menu-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  opacity: 0.35;
}

/* The menu only ever shows on phones — animating two 90px-blurred circles
   there is pure battery drain, so the orbs are static decoration. */
.menu-orb-1 {
  top: -15%;
  left: -10%;
  width: 380px;
  height: 380px;
  background: var(--primary);
}

.menu-orb-2 {
  bottom: -20%;
  right: -15%;
  width: 340px;
  height: 340px;
  background: var(--secondary, #ff6b6b);
  opacity: 0.25;
}

.menu-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse at center, black 35%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 35%, transparent 75%);
  opacity: 0.6;
}

.menu-nav {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-2) 0 var(--space-5);
}

.menu-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-3) 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-family: var(--font-heading);
  font-size: clamp(1.4rem, 6vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: #fff;
  text-decoration: none;
  transition: color 0.25s ease, padding-left 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  animation: linkEnter 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: calc(var(--i) * 60ms + 120ms);
  -webkit-tap-highlight-color: transparent;
}

.menu-link-num {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--primary);
  letter-spacing: 0.1em;
  opacity: 0.7;
  min-width: 2.5ch;
}

.menu-link-text {
  flex: 1;
}

.menu-link-arrow {
  font-size: 1.2rem;
  color: var(--text-dim);
  opacity: 0;
  transform: translateX(-6px);
  transition: all 0.25s ease;
}

.menu-link:active {
  color: var(--primary);
  padding-left: var(--space-2);
}

.menu-link:active .menu-link-arrow {
  color: var(--primary);
  opacity: 1;
  transform: translateX(0);
}

@media (hover: hover) {
  .menu-link:hover {
    color: var(--primary);
    padding-left: var(--space-2);
  }
  .menu-link:hover .menu-link-arrow {
    color: var(--primary);
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes linkEnter {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
}

.menu-foot {
  position: relative;
  z-index: 2;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding-top: var(--space-5);
  animation: linkEnter 0.5s cubic-bezier(0.16, 1, 0.3, 1) 400ms both;
}

.menu-actions {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.btn-menu {
  width: 100%;
  height: 52px !important;
  justify-content: center;
  font-size: 0.8rem !important;
}

.menu-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.menu-lang {
  display: flex;
  gap: 6px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.lang-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 40px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--text-dim);
  text-decoration: none;
  transition: all 0.2s ease;
}

.lang-chip.active {
  background: linear-gradient(135deg, var(--primary), var(--secondary, var(--primary)));
  color: #000;
  box-shadow: 0 4px 14px rgb(var(--primary-channels) / 0.35);
}

.menu-socials {
  display: flex;
  gap: var(--space-2);
}

.menu-social-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--text-muted);
  font-size: 1.05rem;
  transition: all 0.2s ease;
}

@media (hover: hover) {
  .menu-social-btn:hover {
    color: #fff;
    background: linear-gradient(135deg, var(--primary), var(--secondary, var(--primary)));
    border-color: transparent;
    transform: translateY(-2px);
  }
}

/* Keyboard focus — visible ring on every interactive element */
.mobile-menu-btn:focus-visible,
.menu-link:focus-visible,
.lang-btn:focus-visible,
.lang-chip:focus-visible,
.menu-social-btn:focus-visible,
.nav-shop-link:focus-visible,
.btn-nav:focus-visible,
.btn-menu:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

/* Transitions */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.28s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
}

@media (min-width: 480px) {
  .menu-link {
    font-size: 2.4rem;
  }
}

/* Responsive */
.desktop-only { display: flex; }
.mobile-only { display: none; }

@media (max-width: 768px) {
  .desktop-only { display: none !important; }
  .mobile-only { display: flex !important; }

  .nav-inner {
    height: 60px;
  }
}
</style>
